import type React from "react";

import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  IconButton,
  AppBar,
  Typography,
  Avatar,
} from "@mui/material";
import {
  LayoutDashboard,
  LogOut,
  Contact,
  Handshake,
  BookText,
  EllipsisVertical,
  MoveLeft,
  Users,
  Gauge,
  Cog,
} from "lucide-react";
import { useAuth } from "@/context/auth-context";
import { DEFAULT_COLOR, ACTIVE_COLOR } from "@/constants/color";
import logo from "@/assets/logo.png";
import miniLogo from "@/assets/icon.png";

const drawerWidth = 240;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  key: string;
  redirect?: boolean;
}

const navItems: NavItem[] = [
  {
    key: "dashboard",
    label: "Tablero",
    path: "/home",
    icon: <LayoutDashboard size={24} color="white" />,
  },
  {
    key: "leads",
    label: "Contactos",
    path: "/leads",
    icon: <Contact size={24} color="white" />,
  },
  {
    key: "deals",
    label: "Tratos",
    path: "/deals",
    icon: <Handshake size={24} color="white" />,
  },
  {
    key: "reports",
    label: "Reportes",
    path: "/reports",
    icon: <BookText size={24} color="white" />,
  },
  {
    key: "users",
    label: "Usuarios",
    path: "/users",
    icon: <Users size={24} color="white" />,
  },
  {
    key: "ranking",
    label: "Ranking",
    path: "https://gruposinow.com/ranking/tablero.php",
    icon: <Gauge size={24} color="white" />,
    redirect: true,
  },
  {
    key: "control-panel",
    label: "Panel de control",
    path: "https://panel.gruposinow.com/",
    icon: <Cog size={24} color="white" />,
    redirect: true,
  },
];

type Props = {
  open: boolean;
  toggleDrawer: () => void;
};

export default function DesktopNav({ open, toggleDrawer }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{
          // zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "white",
          color: "text.primary",
          boxShadow: "0px 1px 10px rgba(0, 0, 0, 0.05)",
          ml: open ? `${drawerWidth}px` : "72px",
          width: open ? `calc(100% - ${drawerWidth}px)` : `calc(100% - 72px)`,
          transition: "margin-left 0.2s, width 0.2s",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            edge="start"
            sx={{ mr: 2 }}
          >
            <EllipsisVertical />
          </IconButton>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={() => navigate(-1)}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MoveLeft />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1 }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" noWrap>
              {user?.username}
            </Typography>
            <Avatar
              sx={{
                bgcolor: "rgb(234,145,58)",
                width: 32,
                height: 32,
                fontSize: "0.875rem",
              }}
            >
              {user ? user.username[0].toUpperCase() : "U"}
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          width: open ? drawerWidth : 72,
          flexShrink: 0,
          transition: "width 0.2s",
          "& .MuiDrawer-paper": {
            backgroundColor: DEFAULT_COLOR,
            width: open ? drawerWidth : 72,
            boxSizing: "border-box",
            transition: "width 0.2s",
            overflowX: "hidden",
            color: "white",
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            color: "#fff",
            backgroundColor: DEFAULT_COLOR,
            borderColor: DEFAULT_COLOR,
          }}
        >
          <Box>
            <Box
              sx={{
                mt: 2,
                textAlign: "center",
                alignContent: "center",
                justifyContent: "center",
                justifyItems: "center",
                borderRadius: 90,
              }}
            >
              {open ? (
                <img
                  src={logo}
                  alt="logo"
                  width={200}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate("/home")}
                />
              ) : (
                <img
                  src={miniLogo}
                  alt="logo"
                  width={50}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate("/home")}
                />
              )}
            </Box>

            <List>
              {navItems.map((row) => {
                const isSelected = location.pathname.startsWith(row.path);
                return (
                  <ListItemButton
                    key={row.key}
                    onClick={() => {
                      if (row.redirect) {
                        window.open(row.path, "_blank");
                      }
                      return handleNavigation(row.path);
                    }}
                    sx={{
                      mx: open ? 2 : 1,
                      my: 1,
                      borderRadius: 2,
                      ...(isSelected && {
                        boxShadow: 10,
                      }),
                      backgroundColor: isSelected
                        ? ACTIVE_COLOR
                        : "transparent",
                      "&:hover": {
                        backgroundColor: ACTIVE_COLOR,
                        boxShadow: 2,
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <ListItemIcon sx={{ color: "#fff", minWidth: 36 }}>
                      {row.icon}
                    </ListItemIcon>
                    {open && (
                      <ListItemText
                        primary={row.label}
                        primaryTypographyProps={{ fontSize: "1.1rem" }}
                      />
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <List>
              <ListItemButton
                onClick={() => logout()}
                sx={{
                  mx: open ? 2 : 1,
                  my: 1,
                  borderRadius: 2,
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: ACTIVE_COLOR,
                    boxShadow: 2,
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <ListItemIcon sx={{ color: "#fff", minWidth: 36 }}>
                  <LogOut />
                </ListItemIcon>
                {open && <ListItemText primary="Cerrar sesión" />}
              </ListItemButton>
            </List>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}
